import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";

import { useFootballFacilities } from "../../../Entity/api/queries/useFootballFacilities";
import { EntityBranchDetails, Facility } from "../../../Entity/api/types/entityBranches.types";

import { AccordionCollapseIcon, AccordionExpandIcon, BranchFacilityIcon } from "../../assets/icons/entity-icons";

const FacilitiesTab = ({
  isBranchDataLoading,
  branchData,
}: {
  isBranchDataLoading: boolean;
  branchData?: EntityBranchDetails;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEditBranch" });
  const { data: footballFacilities, isLoading: isFootballFacilitiesLoading } = useFootballFacilities();

  const isSectionLoading = isBranchDataLoading || isFootballFacilitiesLoading;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("facilities")}</Typography>
      {isSectionLoading && <CircularProgress sx={styles.loading} />}
      {!isSectionLoading && footballFacilities && (
        <Box sx={styles.accordionContainer}>
          {footballFacilities.map(({ id, name, items: facilities }, idx) => (
            <>
              <FacilityAccordion id={id} name={name} items={facilities} branchFacilities={branchData?.facilities} />
              {idx !== footballFacilities.length - 1 && <Divider flexItem orientation="horizontal" />}
            </>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FacilitiesTab;

const FacilityAccordion = ({
  id,
  name,
  items,
  branchFacilities,
}: {
  id: number;
  name: string;
  items: {
    id: number;
    name: string;
  }[];
  branchFacilities?: Facility[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleAccordionChange = () => setIsExpanded((prev) => !prev);
  return (
    <Accordion key={id} sx={styles.accordion} expanded={isExpanded} onChange={handleAccordionChange}>
      <AccordionSummary>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Typography component="span" sx={styles.accordionTitle}>
            {name}
          </Typography>
          <Box sx={{ marginLeft: "auto" }}>{isExpanded ? <AccordionCollapseIcon /> : <AccordionExpandIcon />}</Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={styles.accordionDetails}>
        {items.map(({ id, name }) => {
          const facilityItem = branchFacilities?.find((facility) => facility.footballFaciliteItemId === id);
          return (
            <Box key={id} sx={styles.facilityItem}>
              {facilityItem && <BranchFacilityIcon />}
              <Typography sx={styles.facilityItemName}>
                {name}
                {facilityItem && <Typography sx={styles.facilityItemQuantity}>{facilityItem.quantity}</Typography>}
              </Typography>
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 16px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  loading: {
    margin: "50px auto",
  },
  accordionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  accordion: {
    borderRadius: "8px",
    background: "var(--White, #FFF)",
    boxShadow: "none",
    border: "none",
    paddingTop: "8px",
    "&:before": {
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      padding: "0px",
      "& .MuiAccordionSummary-content": {
        margin: "0px",
      },
      "& .MuiAccordionSummary-expandIconWrapper": {
        margin: "0px",
      },
      "& .MuiAccordionSummary-contentGutters": {
        margin: "0px",
      },
    },
  },
  accordionTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "28px",
  },
  facilityItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  facilityItemName: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",

    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  facilityItemQuantity: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    textAlign: "center",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    height: "fit-content",
    borderRadius: "16px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Gray-50, #F9FAFB)",
    padding: "2px 8px",
  },
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: 0,
  },
};
